<template>
  <div class="row q-col-gutter-md q-pa-xs">
    <PreviewDialog
      v-model="previewDialog.model"
      :form="previewDialog.form"
      @save="$emit('save', form)"
      @update="$emit('update', form)"
    />

    <div class="col-xs-12">
      <div class="row q-col-gutter-md">
        <div class="col-xs-2">
          <q-input
            id="form_name"
            v-model="form.name"
            stack-label
            dense
            label="Name"
            outlined
            @input="updateFormDescriptor({ descriptor: 'name', value: $event })"
          />
        </div>
        <div class="col-xs-2">
          <enhanced-q-select
            id="form_type"
            :value="form.type_id"
            :options="formTypeOptions"
            :disable="!!form.id"
            dense
            label="Type"
            @input="updateFormDescriptor({ descriptor: 'type_id', value: $event })"
          />
        </div>
        <div class="col-xs-2">
          <enhanced-q-select
            id="form_product_type"
            :value="form.product_type_id"
            :options="productTypeOptions"
            :disable="!!form.product_id || !!form.id"
            label="Product Type"
            clearable
            dense
            @input="
              updateFormDescriptor({
                descriptor: 'product_type_id',
                value: $event
              })
            "
          />
        </div>
        <div class="col-xs-2">
          <enhanced-q-select
            id="form_product_type"
            :value="form.product_id"
            :options="productOptions"
            :disable="!!form.product_type_id || !!form.id"
            label="Product"
            dense
            clearable
            @input="updateFormDescriptor({ descriptor: 'product_id', value: $event })"
          />
        </div>
        <div v-if="form.content" class="col-auto self-center">
          <q-btn v-if="!form.id" size="sm" color="positive" @click="$emit('save', form)">
            Save
          </q-btn>

          <q-btn v-else size="sm" color="positive" @click="$emit('update', form)">
            Update
          </q-btn>

          <q-btn class="q-ml-md" size="sm" color="tertiary" @click="preview(form)">
            Preview
          </q-btn>
        </div>
      </div>
    </div>
    <div class="col-xs-8 dragged-elements">
      <draggable
        :list="form.content"
        :options="{
          group: { name: 'fields', pull: true, put: true },
          handle: '.drag-handle'
        }"
        class="draggable-area"
      >
        <template v-if="!form.content.length" #header>
          <p class="empty">
            Drag fields from the "Available fields' section to add them to your custom form.
          </p>
        </template>

        <lm-draggable-form-component
          v-for="(field, index) in form.content"
          :id="`field_${index}`"
          :key="`field_${index}`"
          :field.sync="form.content[index]"
          :index="index"
          @duplicate="duplicate"
          @remove="remove"
        />
      </draggable>
    </div>
    <div class="col-xs-4">
      <q-list class="fields-list">
        <q-item-label class="q-pa-sm text-center" header>Available fields</q-item-label>
        <q-separator />
        <draggable
          :options="{ group: { name: 'fields', pull: 'clone', put: false } }"
          :value="availableComponents"
          class="elements-wrapper"
        >
          <q-item
            v-for="(field, index) in availableComponents"
            :id="`field_${index}`"
            :key="`field_${index}`"
            class="no-padding"
          >
            <q-item-section class="q-pa-sm">
              <q-item-label lines="1" class="text-weight-medium">
                {{ field.title }}
              </q-item-label>
              <q-item-label>{{ field.description }}</q-item-label>
            </q-item-section>
          </q-item>
        </draggable>
      </q-list>
    </div>
  </div>
</template>

<script>
import { LmFormGenerator } from './lm-form-generator.class'
import * as apiTo from '@/modular-api'
// Libraries
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'
// Components
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import LmDraggableFormComponent from './LmDraggableFormComponent'
import PreviewDialog from './PreviewDialog'

export default {
  components: {
    draggable,
    EnhancedQSelect,
    LmDraggableFormComponent,
    PreviewDialog
  },

  props: {
    formData: Object
  },

  data() {
    return {
      LM_FORM_GENERATOR: new LmFormGenerator(),
      form: {
        name: null,
        type_id: null,
        product_type_id: null,
        product_id: null,
        content: []
      },
      previewDialog: {
        model: false,
        form: {}
      }
    }
  },

  computed: {
    formTypeOptions() {
      if (!this.formData) return
      return this.generateOptions({ collection: this.formData.form_types })
    },

    productTypeOptions() {
      if (!this.formData) return
      return this.generateOptions({
        collection: this.formData.product_types,
        includeNone: true,
        noneLabel: 'All',
        sort: false,
        sortBefore: true,
        sortingOptions: {
          sortDataType: 'string',
          sortBy: 'name',
          sortOrder: 'asc'
        }
      })
    },

    productOptions() {
      if (!this.formData) return
      return this.generateOptions({
        collection: this.formData.products
      })
    },

    availableComponents() {
      return this.LM_FORM_GENERATOR.availableComponents
    }
  },

  watch: {
    formData: {
      handler(newVal) {
        if (newVal && newVal.form) {
          this.form = { ...newVal.form, content: newVal.form.content.filter(f => !!f) }
        }
      },
      immediate: true
    }
  },

  methods: {
    duplicate(index) {
      let clone = cloneDeep(this.form.content[index])
      this.form.content[index].settings.name = this.LM_FORM_GENERATOR.generateName()
      this.form.content[index].settings.open = false
      this.form.content.push(clone)
    },

    remove(index) {
      this.form.content.splice(index, 1)
    },

    updateFormDescriptor(params) {
      this.form[params.descriptor] = params.value

      if (params.descriptor !== 'name' && params.value && !this.form.id) {
        apiTo.CUSTOM_FORM_EXISTS({
          type_id: this.form.type_id,
          product_type_id: this.form.product_type_id,
          product_id: this.form.product_id
        })
      }
    },

    preview(form) {
      this.previewDialog = {
        model: true,
        form: cloneDeep(form)
      }
    }
  }
}
</script>

<style lang="stylus">
@import '~@/styles/stylus/custom-variables'

.q-btn-group {
  box-shadow: none !important
}
.form-field-toolbox {
  border: 1px dotted silver;
  position: relative;
  display: flex;
  width: 100%;
  margin-bottom: 1rem;
}
.fields-list {
  position sticky
  top 20px

  .elements-wrapper {
    padding 10px
    display grid
    gap 7px
  }
}

.draggable-area {
  position relative
  border 1px dashed
  min-width: 100%;
  min-height: 60vh
  padding 10px

  .empty {
    position: absolute;
    height: 100%;
    width: 100%;
    display flex
    justify-content center
    align-items center
  }
}

.dragged-elements .sortable-ghost, .fields-list .q-item {
  padding 15px
  cursor move
  border 1px dotted
}

.dragged-elements .sortable-ghost {
  margin-bottom 15px
  background $tertiary
  color white
}
</style>
